import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { useContext, useEffect, useRef } from 'react';

import { PORTAL_PATH } from '../../../../routes/paths';
import { useMobile } from '../../../../hooks/useMobile';
import WithGlobalOverlay from '../../../../common/global-loader-overlay';

import Logo from './logo';
import Menu from './menu';
import Explore from './explore';
import { DrawerSwitch, DrawerContext } from './drawer';
import { ClientStatusContext } from '../../../../common/client-status';

const HEIGHT_OF_LOGO = '80px';
const HEIGHT_OF_MENU = 'auto';
const HEIGHT_OF_TOGGLE = '72px';

const useStyles = makeStyles((theme: Theme) => ({
  navigation: {
    display: 'grid',
    gridTemplateRows: `${HEIGHT_OF_LOGO} ${HEIGHT_OF_MENU} ${HEIGHT_OF_TOGGLE}`,
    background: theme.palette.primary.dark,
    overflow: 'hidden',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      zIndex: 2000
    }
  },
  menuContainer: {
    overflowY: 'auto'
  }
}));

function useOutsideClick(callback: { (): void }) {
  const ref = useRef<HTMLElement>(null);

  const handleClickOutside = (event: { target: any }) => {
    if (ref.current && !ref.current.contains(event.target)) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  return { ref };
}

const Navigation = () => {
  const classes = useStyles();
  const { isMobile } = useMobile();
  const { pathname } = useLocation();
  const { drawerState, toggleDrawerState } = useContext(DrawerContext);
  const { ref } = useOutsideClick(toggleDrawerState);

  const isShrinked = drawerState === 'shrinked';
  const hasExploreProductsAvailable = useContext(ClientStatusContext).checkIfUserHasPortalPrivilege('EXPLORE_PRODUCTS');

  if (drawerState === 'initialized' || (isMobile && drawerState === 'closed')) {
    return null;
  }

  return (
    <WithGlobalOverlay showBackdrop={isMobile && drawerState === 'open'}>
      <Box className={classes.navigation} ref={isMobile ? ref : undefined}>
        <Box>
          <Logo isShrinked={isShrinked} isSelected={pathname === PORTAL_PATH.DASHBOARD} />
        </Box>
        <Box className={classes.menuContainer} data-testid="navigation">
          <Menu />
          {hasExploreProductsAvailable ? (
            <Explore
              isShrinked={isShrinked}
              isSelected={pathname === PORTAL_PATH.PRODUCTS_AND_SERVICES}
              isMobile={isMobile === true}
              toggleDrawerState={toggleDrawerState}
            />
          ) : (
            <div />
          )}
        </Box>
        <Box>
          <DrawerSwitch />
        </Box>
      </Box>
    </WithGlobalOverlay>
  );
};

export default Navigation;
